// apps/smart-shikka-web/src/components/AppNav.tsx
// PR #767: thin Web binding over the shared Smart Shikka product sidebar. Web and
// Desktop render the same sidebar and the same account menu; Settings is reached
// from the account menu, not from a permanent nav item.
import { useEffect, useState } from "react";
import { useAuth } from "../auth.js";
import {
  ProductAccountMenu,
  ProductSidebar,
  ProductSidebarRail,
  productText,
  useBrandColors,
  type AccountMenuItemId,
  type ProductNavRoute,
} from "@smart-shikka/react";
import { SHIKKA_LOGO, useLocale } from "../ui.js";
import { SidebarUpdateNotice } from "./VersionUpdateNotice.js";

export interface NavSession { id: string; title: string; }

interface AppNavProps {
  activeRoute: ProductNavRoute | "settings" | "admin" | "help" | "support";
  sessions?: NavSession[];
  activeSessionId?: string | null;
  onNewChat?: () => void;
  onSelectSession?: (id: string) => void;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  assistantSlot?: React.ReactNode;
  compactAssistantSlot?: React.ReactNode;
}

/** Canonical hash routes the account menu can reach. */
const ACCOUNT_ROUTES: Partial<Record<AccountMenuItemId, string>> = {
  settings: "#settings",
  appearance: "#settings",
  skills: "#skills",
  ceoAssistant: "#chat",
  adminOrganization: "#admin",
  systemIntegrations: "#admin",
  emergencyStop: "#stop",
  usage: "#usage",
  // Help Center is for every user; staff reach the Support Inbox inside it.
  support: "#help",
};

/** Menu entries that only reach a real surface with super-admin rights. */
const SUPER_ADMIN_ONLY_ROUTES: Partial<Record<AccountMenuItemId, string>> = {
  connections: "#admin",
  integrations: "#admin",
};

/** Entries handled locally instead of by navigation. */
const LOCAL_ACTION_ITEMS: readonly AccountMenuItemId[] = ["logout", "language"];

function resolveAccountRoute(item: AccountMenuItemId, isOwner: boolean): string | undefined {
  return ACCOUNT_ROUTES[item] ?? (isOwner ? SUPER_ADMIN_ONLY_ROUTES[item] : undefined);
}

/**
 * Every entry whose surface this account cannot reach, derived from the same route
 * maps the click handler uses, so any clickable entry really navigates somewhere.
 */
export function unavailableAccountItems(isOwner: boolean): AccountMenuItemId[] {
  const candidates: AccountMenuItemId[] = [
    "settings",
    "appearance",
    "skills",
    "connections",
    "integrations",
    "permissionsTrust",
    "usage",
    "support",
    "ceoAssistant",
    "adminOrganization",
    "systemIntegrations",
    "emergencyStop",
  ];
  return candidates.filter(
    (item) => !LOCAL_ACTION_ITEMS.includes(item) && resolveAccountRoute(item, isOwner) === undefined,
  );
}

export function AppNav({
  activeRoute,
  sessions = [],
  activeSessionId,
  onNewChat,
  onSelectSession,
  collapsed = false,
  onCollapsedChange,
  assistantSlot,
  compactAssistantSlot,
}: AppNavProps) {
  const { account, logout, isOwner, isPrivileged } = useAuth();
  const C = useBrandColors();
  const { locale, setLocale, tr } = useLocale();
  const [notice, setNotice] = useState<string | null>(null);
  const [mobile, setMobile] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const name = account?.email?.split("@")[0] ?? (locale === "hi" ? "उपयोगकर्ता" : "User");
  const isSuperSubAdmin = account?.role === "super-sub-admin" && account.status === "active";

  useEffect(() => {
    const media = window.matchMedia("(max-width: 820px)");
    const update = () => { setMobile(media.matches); if (!media.matches) setDrawerOpen(false); };
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  const navigate = (route: ProductNavRoute) => {
    window.location.hash = `#${route}`;
    setDrawerOpen(false);
  };

  const createNewChat = () => {
    if (onNewChat) onNewChat();
    else window.location.hash = "#chat";
    setDrawerOpen(false);
  };

  const onAccountSelect = (item: AccountMenuItemId) => {
    setNotice(null);
    if (item === "logout") { void logout(); return; }
    if (item === "language") { setLocale(locale === "hi" ? "en" : "hi"); return; }
    const route = resolveAccountRoute(item, isOwner);
    if (!route) { setNotice(productText("unsupportedSurface", locale)); return; }
    window.location.hash = route;
  };

  return (
    <div className={mobile ? "ss-mobile-nav" : "ss-nav"}>
      {mobile && (
        <button
          type="button"
          className="ss-mobile-menu-button"
          aria-label={drawerOpen ? "मेनू बंद करें" : "मेनू खोलें"}
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen((open) => !open)}
        >
          ☰
        </button>
      )}
      {mobile && drawerOpen && (
        <button
          type="button"
          className="ss-mobile-nav-overlay"
          aria-label="मेनू बंद करें"
          onClick={() => setDrawerOpen(false)}
        />
      )}
      <div
        className={mobile ? `ss-mobile-nav-drawer ${drawerOpen ? "is-open" : "is-closed"}` : undefined}
      >
      {!mobile && collapsed ? (
        <ProductSidebarRail
          locale={locale}
          logoSrc={SHIKKA_LOGO}
          activeRoute={activeRoute}
          onNavigate={navigate}
          onNewChat={createNewChat}
          onExpand={() => onCollapsedChange?.(false)}
          assistantSlot={compactAssistantSlot}
          accountSlot={
            <ProductAccountMenu
              compact
              locale={locale}
              displayName={name}
              subtitle={tr("privateAccount")}
              visibility={{ isSuperAdmin: isOwner, isSuperSubAdmin: isSuperSubAdmin, isCeoEligible: isPrivileged }}
              unavailableItems={unavailableAccountItems(isOwner)}
              onSelect={onAccountSelect}
            />
          }
        />
      ) : (
        <ProductSidebar
          locale={locale}
          logoSrc={SHIKKA_LOGO}
          activeRoute={activeRoute}
          onNavigate={navigate}
          onNewChat={createNewChat}
          sessions={sessions.map((s) => ({ id: s.id, title: s.title || tr("anonymousChat") }))}
          activeSessionId={activeSessionId ?? null}
          onSelectSession={(id) => {
            onSelectSession?.(id);
            setDrawerOpen(false);
          }}
          headerSlot={<SidebarUpdateNotice />}
          assistantSlot={assistantSlot}
          {...(!mobile ? { onCollapse: () => onCollapsedChange?.(true) } : {})}
          accountSlot={
            <ProductAccountMenu
              locale={locale}
              displayName={name}
              subtitle={tr("privateAccount")}
              visibility={{ isSuperAdmin: isOwner, isSuperSubAdmin: isSuperSubAdmin, isCeoEligible: isPrivileged }}
              unavailableItems={unavailableAccountItems(isOwner)}
              onSelect={onAccountSelect}
            />
          }
        />
      )}
      </div>
      {notice && (
        <div
          role="status"
          style={{
            position: "absolute",
            bottom: 56,
            left: 8,
            width: 152,
            padding: "6px 8px",
            borderRadius: 8,
            border: `1px solid ${C.border}`,
            background: C.card,
            color: C.muted,
            fontSize: 10,
            lineHeight: 1.4,
            zIndex: 70,
            fontFamily: C.fontSans,
          }}
        >
          {notice}
        </div>
      )}
    </div>
  );
}
